import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from 'react-select';
import { IoMdClose } from "react-icons/io";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import CommonPurchaseReturnFields from "../CommonPurchaseReturnFields";
import debounce from 'lodash/debounce';

const PRPSICreate = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [rows, setRows] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [exchangeRate, setExchangeRate] = useState(0);
    const [exportchargescurrency, setExportchargescurrency] = useState(0);
    const [selectedCurrency, setSelectedCurrency] = useState("$ (USD)");

    const currencyOptions = [
        { value: "", label: "Select Currency" },
        { value: "$ (USD)", label: "USD" },
        { value: "€ (EUR)", label: "EUR" },
        { value: "¥ (JPY)", label: "JPY" },
        { value: "£ (GBP)", label: "GBP" },
        { value: "₹ (INR)", label: "INR" },

    ];

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];

    // Purchase Order Fields State
    const [poData, setPoData] = useState({
        purchaseinvoiceno: '',
        purchaseinvoicedate: '',
        supplierinvoicedate: '',
        supplierinvoiceno: '',
        status: '',
        shipto: '',
        otherreference: '',
        deliverynoteno: '',
        deliverynotedate: '',
        supplier: null,
        supplierDetails: {
            contactperson: '',
            contactno: '',
            email: ''
        },
        supplierMultipleAddress: null,
        branch: null,
        warehouse: null,
        paymentterms: '',
        deliverytime: ''
    });

    const [purchasereturndate, setpurchasereturndate] = useState('');
    const [purchasereturnno, setpurchasereturnno] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const checkpurchasereturnno = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.get(`${BASE_URL}purchaseReturn/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This purchase return number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This purchase return number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking purchase return number:", error);
            setEnquiryValidationMsg('❌ Error validating purchase return number.');
            setIsEnquiryValid(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkpurchasereturnno(purchasereturnno);
        }, 400);
        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [purchasereturnno]);

    // Fetch product data with debounced search
    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 500);

    const getProductData = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data.Index || response.data.data || [];
            setProductOptions(
                products.map(product => ({
                    value: product.id,
                    label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                    partno: product.partno || '',
                    serialnumber: product.serialnumber || '',
                    modelnumber: product.modelnumber || '',
                    tagnumber: product.tagnumber || '',
                    moc: product.moc || '',
                    itemcode: product.itemcode || '',
                    hsnsacno: product.hsnsacno || '',
                    uom: product.unitofmeasurement || 'pcs (pcs)',
                    unitrateinr: product.unitrate || 0,
                }))
            );
        } catch (error) {
            console.error("Error fetching products", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch Product. Please try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        const selectedProductData = productOptions.find(product => product.value === selectedOption.value);

        if (selectedProductData) {
            const newRow = {
                description: selectedProductData.label.split(' - ')[0],
                value: selectedProductData.value,
                productremarks: "",
                quantity: 0,
                unitratecurrency: 0,
                totalcurrency: 0,
                exchangerate: exchangeRate,
                unitRateInr: 0,
                totalInr: 0,
                hsnsacno: selectedProductData.hsnsacno,
                itemcode: selectedProductData.itemcode,
                serialnumber: selectedProductData.serialnumber,
                modelnumber: selectedProductData.modelnumber,
                tagnumber: selectedProductData.tagnumber,
                moc: selectedProductData.moc,
                partno: selectedProductData.partno,
                uom: selectedProductData.uom,
                currency: selectedCurrency,
            };
            setRows([...rows, newRow]);
        }
    };

    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        if (key === 'quantity' || key === 'unitratecurrency' || key === 'exchangerate') {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitratecurrency = parseFloat(updatedRows[index].unitratecurrency) || 0;
            const currentExchangeRate = parseFloat(updatedRows[index].exchangerate) || exchangeRate;

            updatedRows[index].unitRateInr = unitratecurrency * currentExchangeRate;
            updatedRows[index].totalInr = quantity * updatedRows[index].unitRateInr;
            updatedRows[index].totalcurrency = quantity * unitratecurrency;
        }

        setRows(updatedRows);
    };

    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    const validateForm = () => {
        if (!poData.supplier) {
            Swal.fire("Missing Supplier", "Please select a supplier before submitting the form.", "error");
            return false;
        }

        if (!poData.supplierMultipleAddress) {
            Swal.fire("Missing Address", "Please select the supplier's address.", "error");
            return false;
        }

        if (!poData.branch) {
            Swal.fire("Missing Branch", "Please select a branch.", "error");
            return false;
        }

        if (!poData.warehouse) {
            Swal.fire("Missing Warehouse", "Please select a warehouse.", "error");
            return false;
        }

        if (rows.length === 0) {
            Swal.fire("No Products Added", "Please add at least one product to continue.", "error");
            return false;
        }

        for (const row of rows) {
            if (!row.quantity || isNaN(row.quantity) || row.quantity <= 0) {
                Swal.fire(
                    "Invalid Quantity",
                    "Each product must have a valid quantity greater than 0.",
                    "error"
                );
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        setFormSubmitted(true);

        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid purchase return number before submitting.',
            });
            return;
        }

        if (!validateForm()) return;

        setIsLoading(true);

        const misccharges = exportchargescurrency * exchangeRate;
        const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0) + misccharges;
        const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0) + exportchargescurrency;

        const purchaseReturnItem = rows.map(row => ({
            product: { id: row.value },
            productremarks: row.productremarks || '',
            quantity: row.quantity || 0,
            unitrateinr: row.unitRateInr || 0,
            totalinr: row.totalInr || 0,
            unitratecurrency: row.unitratecurrency || 0,
            totalcurrency: row.totalcurrency || 0,
            exchangerate: row.exchangerate || exchangeRate,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            moc: row.moc || '',
            partno: row.partno || '',
            uom: row.uom || '',
            currency: selectedCurrency,
        }));

        const purchaseData = {
            purchaseReturnItem,
            purchasereturntype: 'Pump Spare',
            purchasereturncategory: 'International',
            purchasecurrency: selectedCurrency,
            purchaseexchangerate: exchangeRate,
            grandtotalinr: netTotalInr,
            grandtotalcurrency: netTotalCurrency,
            misccharges,
            exportchargescurrency,
            nettotal: rows.reduce((total, row) => total + (row.totalInr || 0), 0),
            nettotalcurrency: rows.reduce((total, row) => total + (row.totalcurrency || 0), 0),
            supplier: poData.supplier ? { id: poData.supplier.value } : null,
            contactperson: poData.supplierDetails?.contactperson,
            contactno: poData.supplierDetails?.contactno,
            email: poData.supplierDetails?.email,
            supplierMultipleAddress: poData.supplierMultipleAddress ? { id: poData.supplierMultipleAddress.value } : null,
            branch: poData.branch ? { id: poData.branch.value } : null,
            warehouse: poData.warehouse ? { id: poData.warehouse.value } : null,
            purchasereturnno,
            purchasereturndate,
            purchaseinvoiceno: poData.purchaseinvoiceno,
            purchaseinvoicedate: poData.purchaseinvoicedate,
            supplierinvoicedate: poData.supplierinvoicedate,
            supplierinvoiceno: poData.supplierinvoiceno,
            status: poData.status,
            shipto: poData.shipto,
            otherreference: poData.otherreference,
            deliverynoteno: poData.deliverynoteno,
            deliverynotedate: poData.deliverynotedate,
        };

        try {
            const response = await axios.post(`${BASE_URL}purchaseReturn/save`, purchaseData, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Purchase Return created successfully!',
            });
            navigate("/prindex/1");
        } catch (error) {
            console.error("Error creating purchase return:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data || 'Failed to create purchase return. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container">
            <br />
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Purchase Return Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={purchasereturnno}
                                onChange={e => {
                                    setpurchasereturnno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid purchase return number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Purchase Return Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={purchasereturndate}
                                onChange={e => setpurchasereturndate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <CommonPurchaseReturnFields
                    initialData={poData}
                    onDataChange={setPoData}
                    isEditMode={true}
                />

                <hr />
                <br />

                <Row>
                    <Col md="6">
                        <Form.Group controlId="currency">
                            <Form.Label>Currency</Form.Label>
                            <Select
                                value={currencyOptions.find(option => option.value === selectedCurrency)}
                                onChange={(selectedOption) => {
                                    setSelectedCurrency(selectedOption.value);
                                    const updatedRows = rows.map(row => ({
                                        ...row,
                                        currency: selectedOption.value
                                    }));
                                    setRows(updatedRows);
                                }}
                                options={currencyOptions}
                                placeholder="Select Currency"
                                isDisabled={isLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md="6">
                        <Form.Group controlId="exchangeRate">
                            <Form.Label>Exchange Rate</Form.Label>
                            <Form.Control
                                type="number"
                                value={exchangeRate || ''}
                                onChange={(e) => {
                                    setExchangeRate(e.target.value);
                                    const updatedRows = rows.map(row => ({
                                        ...row,
                                        exchangerate: e.target.value,
                                        unitRateInr: (row.unitratecurrency || 0) * e.target.value,
                                        totalInr: (row.quantity || 0) * (row.unitratecurrency || 0) * e.target.value
                                    }));
                                    setRows(updatedRows);
                                }}
                                disabled={isLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <hr />
                <br />

                <Row>
                    <Col md='12'>
                        <Form.Group>
                            <Form.Label>Select Product</Form.Label>
                            {isLoading ? (
                                <div className="d-flex align-items-center">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <span>Loading products...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedProduct}
                                    onChange={handleProductChange}
                                    options={productOptions}
                                    placeholder="Search and select product"
                                    onInputChange={handleProductSearchChange}
                                    isClearable
                                    isLoading={isLoading}
                                />
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <br />

                <div className="table-responsive">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Product Description</th>
                                <th>Serial No.</th>
                                <th>Model No.</th>
                                <th>Tag No.</th>
                                <th>Part No.</th>
                                <th>MOC</th>
                                <th>Item Code</th>
                                <th>HSN/SAC No.</th>
                                <th>Quantity</th>
                                <th>Unit of Measurement</th>
                                <th>Currency</th>
                                <th>Unit Rate (Currency)</th>
                                <th>Total (Currency)</th>
                                <th>Exchange Rate</th>
                                <th>Unit Price (INR)</th>
                                <th>Total Amount (INR)</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td>
                                        {row.description}
                                        <Form.Control as="textarea" rows={2}
                                            className="remarks"
                                            type="text"
                                            value={row.productremarks}
                                            onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                            placeholder="Enter item remarks"
                                            disabled={isLoading}
                                        />
                                    </td>
                                    <td>{row.serialnumber}</td>
                                    <td>{row.modelnumber}</td>
                                    <td>{row.tagnumber}</td>
                                    <td>{row.partno}</td>
                                    <td>{row.moc}</td>
                                    <td>{row.itemcode}</td>
                                    <td>{row.hsnsacno}</td>
                                    <td>
                                        <Form.Control
                                            min="0"
                                            className="quantity"
                                            type="number"
                                            value={row.quantity || ''}
                                            onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className="uom"
                                            value={unitOptions.find(option => option.value === row.uom)}
                                            onChange={(selectedOption) => handleRowChange(index, 'uom', selectedOption.value)}
                                            options={unitOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                            isDisabled={isLoading}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className="currency"
                                            value={currencyOptions.find(option => option.value === row.currency)}
                                            options={[currencyOptions.find(option => option.value === row.currency)]}
                                            isDisabled
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            min="0"
                                            className="unitrate"
                                            type="number"
                                            value={row.unitratecurrency || ''}
                                            onChange={(e) => handleRowChange(index, 'unitratecurrency', e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </td>
                                    <td>{(row.totalcurrency || 0).toFixed(2)}</td>
                                    <td>
                                        <Form.Control
                                            min="0"
                                            type="number"
                                            value={row.exchangerate || exchangeRate}
                                            onChange={(e) => handleRowChange(index, 'exchangerate', e.target.value)}
                                            className="exchangerate"
                                            disabled={isLoading}
                                        />
                                    </td>
                                    <td>{(row.unitRateInr || 0).toFixed(2)}</td>
                                    <td>{(row.totalInr || 0).toFixed(2)}</td>
                                    <td>
                                        <button
                                            className="button-70 btn-danger"
                                            onClick={() => removeRow(index)}
                                            disabled={isLoading}
                                        >
                                            <IoMdClose />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <div className="card mt-3" style={{ width: '50%', marginLeft: 'auto' }}>
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Purchase Summary</h5>
                    </div>
                    <div className="card-body">
                        <div className="row mb-2">
                            <div className="col-8 text-end">
                                <strong>Net Total (INR)</strong>
                            </div>
                            <div className="col-4">
                                <strong>{rows.reduce((total, row) => total + (row.totalInr || 0), 0).toFixed(2)}</strong>
                            </div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-8 text-end">
                                <strong>Net Total In (Currency)</strong>
                            </div>
                            <div className="col-4">
                                <strong>{rows.reduce((total, row) => total + (row.totalcurrency || 0), 0).toFixed(2)}</strong>
                            </div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-8 text-end">
                                <strong>Export Charges (Currency)</strong>
                            </div>
                            <div className="col-4">
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={exportchargescurrency || ''}
                                    onChange={(e) => setExportchargescurrency(parseFloat(e.target.value) || 0)}
                                    className="form-control-sm"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-8 text-end">
                                <strong>Export Charges (INR)</strong>
                            </div>
                            <div className="col-4">
                                <strong>{(exportchargescurrency * exchangeRate).toFixed(2)}</strong>
                            </div>
                        </div>

                        <div className="row mt-3 pt-2 border-top">
                            <div className="col-8 text-end">
                                <h5>Grand Total (INR)</h5>
                            </div>
                            <div className="col-4">
                                <h5>{
                                    (rows.reduce((total, row) => total + (row.totalInr || 0), 0) +
                                        (exportchargescurrency * exchangeRate)
                                    ).toFixed(2)}</h5>
                            </div>
                        </div>

                        <div className="row mt-2">
                            <div className="col-8 text-end">
                                <h5>Grand Total In (Currency)</h5>
                            </div>
                            <div className="col-4">
                                <h5>{
                                    (rows.reduce((total, row) => total + (row.totalcurrency || 0), 0) +
                                        exportchargescurrency
                                    ).toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                </div>

                <br />
                <div className="d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading || !isEnquiryValid}
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Processing...</span>
                            </>
                        ) : 'Create Purchase Return'}
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default PRPSICreate;